import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const ORDER_STATUSES = ['pending', 'paid', 'processing', 'shipped', 'on_hold', 'completed', 'cancelled'];
const STATUS_COLOURS = {
  pending: '#f0ad4e', paid: '#5bc0de', processing: '#337ab7',
  shipped: '#5cb85c', on_hold: '#d9534f', completed: '#28a745', cancelled: '#999',
};

function useAdminAuth() {
  const [creds, setCreds] = useState(() => {
    const saved = sessionStorage.getItem('adminCreds');
    return saved ? JSON.parse(saved) : null;
  });

  const login = (username, password) => {
    const c = { username, password };
    sessionStorage.setItem('adminCreds', JSON.stringify(c));
    setCreds(c);
  };

  const logout = () => {
    sessionStorage.removeItem('adminCreds');
    setCreds(null);
  };

  const authHeader = creds
    ? { Authorization: 'Basic ' + btoa(`${creds.username}:${creds.password}`) }
    : {};

  return { creds, login, logout, authHeader };
}

function LoginForm({ onLogin }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API.replace('/api', '')}/admin-api/products`, {
        headers: { Authorization: 'Basic ' + btoa(`${u}:${p}`) }
      });
      onLogin(u, p);
    } catch {
      setError('Invalid username or password.');
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px', marginBottom: '12px',
    border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ maxWidth: '360px', margin: '80px auto', fontFamily: 'sans-serif', padding: '20px' }}>
      <h2>Admin Login</h2>
      <form onSubmit={handleSubmit}>
        <input placeholder="Username" style={inputStyle} value={u} onChange={e => setU(e.target.value)} />
        <input type="password" placeholder="Password" style={inputStyle} value={p} onChange={e => setP(e.target.value)} />
        {error && <p style={{ color: 'red' }}>{error}</p>}
        <button type="submit" style={{ width: '100%', padding: '12px', background: '#111',
          color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px' }}>
          Login
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const { creds, login, logout, authHeader } = useAdminAuth();
  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '', description: '', base_price: '', stock_quantity: '999',
    personalisation_enabled: false, is_active: true,
  });
  const [productImage, setProductImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const ADMIN_API = API.replace('/api', '') + '/admin-api';

  const loadOrders = useCallback(async () => {
    const res = await axios.get(`${ADMIN_API}/orders`, { headers: authHeader });
    setOrders(res.data);
  }, [authHeader, ADMIN_API]);

  const loadProducts = useCallback(async () => {
    const res = await axios.get(`${ADMIN_API}/products`, { headers: authHeader });
    setProducts(res.data);
  }, [authHeader, ADMIN_API]);

  useEffect(() => {
    if (!creds) return;
    loadOrders();
    loadProducts();
  }, [creds, loadOrders, loadProducts]);

  const handleStatusChange = async (orderId, status) => {
    await axios.patch(`${ADMIN_API}/orders/${orderId}/status`,
      { status }, { headers: authHeader });
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData();
    Object.entries(newProduct).forEach(([k, v]) => formData.append(k, v));
    if (productImage) formData.append('image', productImage);
    await axios.post(`${ADMIN_API}/products`, formData, { headers: authHeader });
    setNewProduct({ name: '', description: '', base_price: '', stock_quantity: '999',
      personalisation_enabled: false, is_active: true });
    setProductImage(null);
    await loadProducts();
    setSaving(false);
    alert('Product created!');
  };

  const handleToggleActive = async (product) => {
    const formData = new FormData();
    formData.append('is_active', !product.is_active);
    await axios.patch(`${ADMIN_API}/products/${product.id}`, formData, { headers: authHeader });
    await loadProducts();
  };

  if (!creds) return <LoginForm onLogin={login} />;

  const tabStyle = (t) => ({
    padding: '10px 24px', cursor: 'pointer', border: 'none',
    borderBottom: tab === t ? '3px solid #111' : '3px solid transparent',
    background: 'none', fontSize: '16px', fontWeight: tab === t ? 'bold' : 'normal',
  });

  const inputStyle = {
    width: '100%', padding: '9px', marginBottom: '10px',
    border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ fontFamily: 'sans-serif', maxWidth: '1100px', margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Admin Panel</h2>
        <button onClick={logout} style={{ padding: '8px 20px', background: '#eee',
          border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Logout</button>
      </div>

      <div style={{ borderBottom: '1px solid #ddd', marginBottom: '24px' }}>
        <button style={tabStyle('orders')} onClick={() => setTab('orders')}>Orders</button>
        <button style={tabStyle('products')} onClick={() => setTab('products')}>Products</button>
      </div>

      {/* ── ORDERS TAB ── */}
      {tab === 'orders' && (
        <div>
          <h3>{orders.length} Orders</h3>
          {orders.length === 0 && <p style={{ color: '#999' }}>No orders yet.</p>}
          {orders.map(order => (
            <div key={order.id} style={{
              border: '1px solid #e0e0e0', borderRadius: '10px',
              marginBottom: '12px', overflow: 'hidden',
            }}>
              <div onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 18px', cursor: 'pointer', background: '#fafafa' }}>
                <div>
                  <strong>{order.shipping_name}</strong>
                  <span style={{ color: '#666', marginLeft: '12px', fontSize: '14px' }}>
                    {order.shipping_email}
                  </span>
                  <span style={{ marginLeft: '12px', fontSize: '14px', color: '#888' }}>
                    {order.created_at ? new Date(order.created_at).toLocaleDateString('en-GB') : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <strong>£{order.total_price?.toFixed(2)}</strong>
                  <select
                    value={order.status}
                    onChange={e => { e.stopPropagation(); handleStatusChange(order.id, e.target.value); }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      padding: '6px 10px', borderRadius: '6px', border: 'none',
                      background: STATUS_COLOURS[order.status] || '#eee',
                      color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px',
                    }}>
                    {ORDER_STATUSES.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}</option>
                    ))}
                  </select>
                  <span>{expandedOrder === order.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {expandedOrder === order.id && (
                <div style={{ padding: '16px 18px', borderTop: '1px solid #eee' }}>
                  <p><strong>Shipping address:</strong> {order.shipping_address}</p>
                  {order.wallet_address && (
                    <p><strong>Wallet:</strong> <code style={{ fontSize: '12px' }}>{order.wallet_address}</code></p>
                  )}
                  <p><strong>Order ID:</strong> <code style={{ fontSize: '12px' }}>{order.id}</code></p>
                  <h4 style={{ marginBottom: '8px' }}>Items:</h4>
                  {order.items?.map((item, i) => (
                    <div key={i} style={{ padding: '8px', background: '#f9f9f9',
                      borderRadius: '6px', marginBottom: '6px', fontSize: '14px' }}>
                      <strong>Product ID:</strong> {item.product_id} —
                      <strong> Qty:</strong> {item.quantity} —
                      <strong> Price:</strong> £{item.unit_price?.toFixed(2)}
                      {item.preset_design_id && <span> — <strong>Design:</strong> {item.preset_design_id}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── PRODUCTS TAB ── */}
      {tab === 'products' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          <div>
            <h3>Add New Product</h3>
            <form onSubmit={handleCreateProduct}>
              <input placeholder="Product name *" style={inputStyle} required
                value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} />
              <textarea placeholder="Description" style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
                value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} />
              <input type="number" placeholder="Price (£) *" style={inputStyle} required step="0.01" min="0"
                value={newProduct.base_price} onChange={e => setNewProduct({ ...newProduct, base_price: e.target.value })} />
              <input type="number" placeholder="Stock quantity" style={inputStyle} min="0"
                value={newProduct.stock_quantity} onChange={e => setNewProduct({ ...newProduct, stock_quantity: e.target.value })} />
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newProduct.personalisation_enabled}
                    onChange={e => setNewProduct({ ...newProduct, personalisation_enabled: e.target.checked })} />
                  Enable preset design selection for this product
                </label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newProduct.is_active}
                    onChange={e => setNewProduct({ ...newProduct, is_active: e.target.checked })} />
                  Active (visible to customers)
                </label>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px' }}>Product photo:</label>
                <input type="file" accept="image/*" onChange={e => setProductImage(e.target.files[0])} />
              </div>
              <button type="submit" disabled={saving}
                style={{ width: '100%', padding: '12px', background: '#111', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '15px' }}>
                {saving ? 'Saving...' : 'Create Product'}
              </button>
            </form>
          </div>

          <div>
            <h3>Existing Products ({products.length})</h3>
            {products.map(p => (
              <div key={p.id} style={{
                border: '1px solid #e0e0e0', borderRadius: '8px',
                padding: '14px', marginBottom: '12px',
                opacity: p.is_active ? 1 : 0.5,
              }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {p.image_url
                    ? <img src={p.image_url} alt={p.name}
                        style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px' }} />
                    : <div style={{ width: '60px', height: '60px', background: '#f0f0f0',
                        borderRadius: '6px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '24px' }}>🔑</div>
                  }
                  <div style={{ flex: 1 }}>
                    <strong>{p.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#666' }}>{p.description}</p>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>£{p.base_price?.toFixed(2)} — Stock: {p.stock_quantity}</p>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                      {p.personalisation_enabled && (
                        <span style={{ fontSize: '12px', background: '#e3f2fd',
                          padding: '2px 8px', borderRadius: '4px' }}>🎨 Design selection on</span>
                      )}
                      <button onClick={() => handleToggleActive(p)}
                        style={{ fontSize: '12px', padding: '4px 10px',
                          background: p.is_active ? '#dc3545' : '#28a745',
                          color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                        {p.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
